import { BillingActionRequestPayload } from '@/lib/api/financial/billing-action-request-api'
import {
    MdHourglassTop,
    MdVerified,
    MdVisibility,
    MdWarning,
} from 'react-icons/md'

/**
 * Laudo da leitura do comprovante feita pelo cron `receipt-review-tasks`.
 *
 * Sem `receiptReviewedAt` a IA ainda não passou (o cron roda a cada 10 minutos).
 * Quando passou, o operador vê num relance se o documento é confiável e, se não for,
 * exatamente o que a IA achou de errado — é essa a informação que decide a aprovação.
 *
 * O veredito informa; quem decide é o operador olhando o documento, por isso o botão
 * "Ver comprovante" acompanha todos os estados.
 */
export default function ReceiptReview({
    payload,
    hasReceipt,
    onOpenReceipt,
}: {
    payload?: BillingActionRequestPayload | null
    hasReceipt?: boolean
    onOpenReceipt?: () => void
}) {
    if (!hasReceipt) {
        return (
            <div className="rounded-lg bg-base-200/50 px-3 py-2 text-sm text-base-content/50">
                Sem comprovante anexado
            </div>
        )
    }

    const openButton = onOpenReceipt && (
        <button
            type="button"
            onClick={onOpenReceipt}
            className="btn btn-xs btn-ghost gap-1 text-secondary"
        >
            <MdVisibility className="text-sm" />
            Ver comprovante
        </button>
    )

    const findings = payload?.receiptFindings || []

    if (!payload?.receiptReviewedAt) {
        return (
            <div className="rounded-lg bg-base-200/50 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                <span className="badge badge-ghost gap-1">
                    <MdHourglassTop className="text-sm" />
                    Leitura pendente
                </span>
                {openButton}
            </div>
        )
    }

    if (payload.receiptReadable === false) {
        return (
            <div className="rounded-lg bg-warning/10 border border-warning/30 px-3 py-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="badge badge-warning gap-1">
                        <MdWarning className="text-sm" />
                        Comprovante ilegível
                    </span>
                    {openButton}
                </div>
                {findings[0] && (
                    <p className="text-xs text-base-content/70 mt-1.5">
                        {findings[0]}
                    </p>
                )}
            </div>
        )
    }

    if (payload.receiptTrustworthy) {
        const receipt = payload.receiptData
        return (
            <div className="rounded-lg bg-success/10 border border-success/30 px-3 py-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="badge badge-success text-white gap-1">
                        <MdVerified className="text-sm" />
                        Comprovante validado
                    </span>
                    {openButton}
                </div>
                {receipt?.banco && (
                    <p className="text-xs text-base-content/70 mt-1.5">
                        {receipt.banco}
                        {receipt.pagador ? ` · ${receipt.pagador}` : ''}
                    </p>
                )}
            </div>
        )
    }

    return (
        <div className="rounded-lg bg-error/10 border border-error/30 px-3 py-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="badge badge-error text-white gap-1">
                    <MdWarning className="text-sm" />
                    Comprovante suspeito
                    {findings.length > 0 ? ` (${findings.length})` : ''}
                </span>
                {openButton}
            </div>
            {findings.length > 0 ? (
                <ul className="list-disc list-inside text-xs text-base-content/70 mt-1.5 space-y-0.5">
                    {findings.map((finding, i) => (
                        <li key={i}>{finding}</li>
                    ))}
                </ul>
            ) : (
                <p className="text-xs text-base-content/70 mt-1.5">
                    A IA não conseguiu validar o comprovante.
                </p>
            )}
        </div>
    )
}
